import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ASSESSMENT } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Send, CheckCircle2, Target, TrendingUp } from "lucide-react";

export default function AssessmentPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [path, setPath] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [turns, setTurns] = useState([]); // {role, content, note?}
  const [answer, setAnswer] = useState("");
  const [phase, setPhase] = useState("intro"); // intro | active | complete
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get(`/paths/${slug}`).then((r) => setPath(r.data)); }, [slug]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/assessment/start", { path_slug: slug });
      setAssessmentId(data.assessment_id);
      setTurns([{ role: "assistant", content: data.question }]);
      setPhase("active");
    } catch { toast.error("Could not start assessment"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!answer.trim() || !assessmentId) return;
    const userAnswer = answer;
    setAnswer("");
    setTurns((t) => [...t, { role: "user", content: userAnswer }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assessment/answer", { assessment_id: assessmentId, answer: userAnswer });
      if (data.phase === "complete") {
        setTurns((t) => [...t, { role: "assistant", content: data.summary || "Assessment complete!", complete: true }]);
        setResult(data);
        setPhase("complete");
      } else {
        setTurns((t) => [...t, { role: "assistant", content: data.question, note: data.note }]);
      }
    } catch { toast.error("Assessment step failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={ASSESSMENT.root} className="space-y-6 animate-fade-up">
      <Link to={`/app/paths/${slug}`} className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to path
      </Link>

      <div>
        <div className="label-mono text-primary">// AI skills assessment</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
          {path?.title || "Path"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A short conversational check to tailor your roadmap. No pressure — be honest.
        </p>
      </div>

      {phase === "intro" && (
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <div className="font-display text-xl font-bold">Before we begin</div>
          </div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• 3–5 short questions.</li>
            <li>• Your mentor will decide your level and identify strengths / weaknesses.</li>
            <li>• A personalized roadmap will be built for you — no generic beginner/intermediate/advanced label.</li>
          </ul>
          <Button
            data-testid={ASSESSMENT.startBtn}
            onClick={start}
            disabled={busy}
            className="mt-6 rounded-full btn-neon font-mono text-xs uppercase tracking-[0.2em]"
          >
            {busy ? "Starting…" : "Start assessment"} <Sparkles className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      {phase !== "intro" && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
            {turns.map((t, i) => (
              <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${t.role === "user" ? "border border-border bg-muted" : t.complete ? "border border-accent/40 bg-accent/10" : "border border-primary/30 bg-primary/5"}`}>
                  {t.note && <div className="label-mono mb-2 text-primary">{t.note}</div>}
                  {t.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="text-sm text-muted-foreground"><span className="chat-thinking">Thinking</span></div>
            )}
          </div>

          {phase === "active" && (
            <div className="mt-6 border-t border-border pt-4">
              <Textarea
                data-testid={ASSESSMENT.answerInput}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer…"
                className="min-h-[80px] rounded-lg"
                disabled={busy}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
              />
              <div className="mt-3 flex items-center justify-between">
                <div className="label-mono">⌘/Ctrl + Enter to send</div>
                <Button
                  data-testid={ASSESSMENT.submitAnswerBtn}
                  onClick={submit}
                  disabled={!answer.trim() || busy}
                  className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
                >
                  <Send className="mr-2 h-4 w-4" /> Send
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {phase === "complete" && result && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6">
              <div className="label-mono">Recommended level</div>
              <div className="mt-2 font-display text-3xl font-black tracking-tighter">{result.level}</div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="label-mono flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Strengths</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(result.strengths || []).map((s, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-accent" /> {s}</li>)}
                {(!result.strengths || result.strengths.length === 0) && <li className="text-xs text-muted-foreground">None detected yet.</li>}
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="label-mono flex items-center gap-1"><Target className="h-3.5 w-3.5" /> To improve</div>
              <ul className="mt-2 space-y-1 text-sm">
                {(result.weaknesses || []).map((s, i) => <li key={i}>• {s}</li>)}
                {(!result.weaknesses || result.weaknesses.length === 0) && <li className="text-xs text-muted-foreground">Nothing critical.</li>}
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="label-mono">// personalized roadmap</div>
            <div className="mt-3 space-y-3">
              {(result.roadmap || []).map((r, i) => {
                const idx = r.lesson_index;
                const title = path?.lessons?.[idx] || `Lesson ${idx + 1}`;
                return (
                  <Link key={i} to={`/app/paths/${slug}/lessons/${idx}`} className="group flex items-center gap-4 rounded-xl border border-border p-4 hover:border-primary/50 transition">
                    <div className="label-mono w-8">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1">
                      <div className="font-display font-bold">{title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{r.reason}</div>
                    </div>
                    <div className="label-mono opacity-0 transition group-hover:opacity-100">start →</div>
                  </Link>
                );
              })}
            </div>
            <Button
              data-testid={ASSESSMENT.viewRoadmapBtn}
              className="mt-6 rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
              onClick={() => {
                const firstIdx = result.roadmap?.[0]?.lesson_index ?? 0;
                navigate(`/app/paths/${slug}/lessons/${firstIdx}`);
              }}
            >
              Start first lesson <Sparkles className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
